"use client";

import { useState, useEffect, useContext } from "react";
import Image from "next/image";
import { Sparkles, ArrowRight, Star } from "lucide-react";
import { useSession } from "next-auth/react";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { ModalContext } from "@/components/modals/providers";
import { cn } from "@/lib/utils";
import { ReviewItem, getReviews } from "@/actions/reviews";

// Card color schemes and initial alternating rotations matching Shadcn Studio Testimonials-19 exactly
const CARD_THEMES = [
  {
    bg: "bg-[#fef6ed] border-[#fbe5c8] dark:bg-[#281f14] dark:border-[#4d3a22]",
    rotation: "sm:-rotate-2",
  },
  {
    bg: "bg-[#effbf5] border-[#cbf2de] dark:bg-[#11241a] dark:border-[#204a35]",
    rotation: "sm:rotate-1",
  },
  {
    bg: "bg-[#fef0f2] border-[#fbd4da] dark:bg-[#281418] dark:border-[#4f242d]",
    rotation: "sm:-rotate-2",
  },
  {
    bg: "bg-[#eef7fd] border-[#cbe6fb] dark:bg-[#132332] dark:border-[#234563]",
    rotation: "sm:rotate-1",
  },
  {
    bg: "bg-[#f6f2fd] border-[#ded0fa] dark:bg-[#201533] dark:border-[#41286b]",
    rotation: "sm:-rotate-1",
  },
  {
    bg: "bg-[#eef2ff] border-[#ced6fe] dark:bg-[#151933] dark:border-[#28326a]",
    rotation: "sm:rotate-2",
  },
];

interface TestimonialsProps {
  id?: string;
  className?: string;
  containerClassName?: string;
  initialReviews?: ReviewItem[];
  productName?: string;
  productId?: string;
}

export default function Testimonials({
  id,
  className,
  containerClassName,
  initialReviews,
  productName,
  productId,
}: TestimonialsProps) {
  const { data: session, status } = useSession();
  const { setShowSignInModal, setShowReviewModal } = useContext(ModalContext);
  const [reviews, setReviews] = useState<ReviewItem[]>(initialReviews ?? []);
  const [pendingReview, setPendingReview] = useState(false);

  // Sync if initialReviews changes or fetch if undefined
  useEffect(() => {
    if (initialReviews !== undefined) {
      setReviews(initialReviews);
    } else {
      getReviews(productId || productName ? { productId, productName } : undefined)
        .then((items) => {
          if (items) {
            setReviews(items);
          }
        })
        .catch((err) => {
          console.error("Failed to load reviews from DB:", err);
        });
    }
  }, [initialReviews, productId, productName]);

  // Check if user just authenticated and had a pending review request
  useEffect(() => {
    if (status === "authenticated") {
      const savedIntent =
        typeof window !== "undefined"
          ? sessionStorage.getItem("open_review_on_auth")
          : null;

      if (pendingReview || savedIntent === "true") {
        setPendingReview(false);
        const storedDefaultProduct =
          typeof window !== "undefined"
            ? sessionStorage.getItem("review_default_product")
            : null;
        if (typeof window !== "undefined") {
          sessionStorage.removeItem("open_review_on_auth");
          sessionStorage.removeItem("review_default_product");
        }
        setShowReviewModal(true, productName || storedDefaultProduct || undefined);
      }
    }
  }, [status, pendingReview, setShowReviewModal, productName]);

  const handleDropReview = () => {
    if (status !== "authenticated" || !session?.user) {
      if (typeof window !== "undefined") {
        sessionStorage.setItem("open_review_on_auth", "true");
        if (productName) {
          sessionStorage.setItem("review_default_product", productName);
        }
      }
      setPendingReview(true);
      setShowSignInModal(true);
      return;
    }
    setShowReviewModal(true, productName);
  };

  // Build continuous marquee array ensuring seamless looping without blank gaps
  const marqueeReviews = (() => {
    if (!reviews || reviews.length < 4) return [];
    let items = [...reviews];
    while (items.length < 12) {
      items = [...items, ...reviews];
    }
    return items;
  })();

  const renderReviewCard = (item: ReviewItem, index: number, extraClassName?: string) => {
    const theme = CARD_THEMES[index % CARD_THEMES.length];
    const avatarUrl =
      item.image ||
      `https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(
        item.displayName
      )}`;

    const helpertagParts = item.helpertag.includes(" at ")
      ? item.helpertag.split(" at ")
      : null;

    return (
      <div
        className={cn(
          "group relative flex flex-col h-full rounded-2xl sm:rounded-3xl border p-5 sm:p-6 shadow-xs transition-all duration-300 ease-out hover:rotate-0 hover:scale-[1.02] hover:shadow-lg",
          theme.bg,
          theme.rotation,
          extraClassName
        )}
      >
        {/* User Header */}
        <div className="flex items-center gap-3">
          <div className="relative size-10 sm:size-11 overflow-hidden rounded-full border border-border/40 bg-background/80 shadow-2xs flex-shrink-0">
            <Image
              src={avatarUrl}
              alt={item.displayName}
              width={44}
              height={44}
              className="size-full object-cover"
              unoptimized
            />
          </div>
          <div className="flex flex-col min-w-0">
            <h4 className="text-sm sm:text-[15px] font-medium text-foreground tracking-tight truncate">
              {item.displayName}
            </h4>
            {helpertagParts ? (
              <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 truncate">
                {helpertagParts[0]} at{" "}
                <span className="font-semibold text-foreground">
                  {helpertagParts[1]}
                </span>
              </p>
            ) : (
              <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 truncate">
                {item.helpertag}
              </p>
            )}
          </div>
        </div>

        {/* Review Body */}
        <p className="mt-3.5 text-xs sm:text-[13.5px] leading-relaxed text-muted-foreground font-normal">
          &ldquo;{item.review}&rdquo;
        </p>
      </div>
    );
  };

  return (
    <section
      id={id}
      className={cn("pb-14 sm:pb-10 overflow-hidden bg-background", className)}
    >
      <MaxWidthWrapper className={containerClassName}>
        {/* Header Section */}
        <div className="flex flex-col items-center text-center sm:items-start sm:text-left mb-8 sm:mb-10">
          <div className="space-y-2.5">
            {/* Pill Badge */}
            <div>
              <span className="inline-flex w-fit shrink-0 items-center justify-center whitespace-nowrap rounded-full border border-border/80 bg-background px-3 py-0.5 text-xs font-normal text-foreground shadow-2xs">
                Testimonials
              </span>
            </div>

            {/* Headline with exact gradient bar from-primary to-transparent */}
            <h2
              id={id ? `${id}-heading` : undefined}
              className="text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl text-foreground font-urban"
            >
              Real Stories,{" "}
              <span className="relative inline-block">
                Real People
                <span
                  className="absolute -bottom-1 left-0 h-0.5 w-full rounded-full bg-gradient-to-r from-primary via-primary/80 to-transparent"
                  aria-hidden="true"
                />
              </span>
            </h2>

            {/* Subtitle */}
            <p className="text-muted-foreground text-sm sm:text-base max-w-xl">
              {productName
                ? `Check out what our verified players are saying about ${productName}!`
                : "Check out what our awesome competitive players are saying about Foulware macros & cheats!"}
            </p>
          </div>
        </div>

        {/* Adaptive Review Layout: Marquee (>=4), Centered Grid (1-3), or Empty State (0) */}
        {reviews.length >= 4 ? (
          <div className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_5%,black_95%,transparent)] py-3">
            <div className="flex w-max gap-4 sm:gap-5 animate-marquee hover:[animation-play-state:paused]">
              {marqueeReviews.map((item, index) => (
                <div
                  key={`${item.id || item.displayName}-${index}`}
                  className="w-[260px] sm:w-[280px] md:w-[285px] lg:w-[290px] xl:w-[295px] flex-shrink-0 py-2"
                >
                  {renderReviewCard(item, index)}
                </div>
              ))}
            </div>
          </div>
        ) : reviews.length > 0 ? (
          <div className="py-3">
            {reviews.length === 1 && (
              <div className="mx-auto max-w-md w-full py-2">
                {renderReviewCard(reviews[0], 0)}
              </div>
            )}
            {reviews.length === 2 && (
              <div className="mx-auto max-w-2xl w-full grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 py-2">
                {reviews.map((item, index) => (
                  <div key={item.id || index}>
                    {renderReviewCard(item, index)}
                  </div>
                ))}
              </div>
            )}
            {reviews.length === 3 && (
              <div className="mx-auto max-w-4xl w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 py-2">
                {reviews.map((item, index) => (
                  <div key={item.id || index}>
                    {renderReviewCard(item, index)}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-muted/20 py-10 px-6 text-center max-w-2xl mx-auto my-2">
            <div className="flex size-11 items-center justify-center rounded-full bg-primary/10 text-primary mb-3">
              <Star className="size-5 fill-primary text-primary" />
            </div>
            <h3 className="font-semibold text-foreground text-base sm:text-lg">
              No reviews yet for {productName || "this product"}
            </h3>
            <p className="mt-1 max-w-sm text-xs sm:text-sm text-muted-foreground">
              Be the first to share your experience with {productName || "this product"} and earn a $10 account balance credit!
            </p>
          </div>
        )}

        {/* Existing User Incentive Callout Banner */}
        <div className="mt-10 sm:mt-12 flex justify-center">
          <div className="relative flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6 rounded-2xl border border-border/80 bg-gradient-to-r from-card via-muted/20 to-card p-5 sm:px-7 sm:py-5 shadow-xs max-w-3xl w-full">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3.5 text-center sm:text-left">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 shadow-2xs">
                <Sparkles className="size-5" />
              </div>
              <div>
                <h4 className="text-sm sm:text-base font-bold text-foreground">
                  Existing {productName || "Foulware"} user?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                  Drop a review and get an{" "}
                  <span className="font-semibold text-foreground">
                    exclusive discount
                  </span>{" "}
                  on your next purchase.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleDropReview}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-foreground px-5 py-2.5 text-xs sm:text-sm font-semibold text-background shadow-xs hover:opacity-90 transition-opacity cursor-pointer"
            >
              <Star className="size-3.5 fill-current" />
              <span>Drop a Review</span>
              <ArrowRight className="size-3.5" />
            </button>
          </div>
        </div>
      </MaxWidthWrapper>
    </section>
  );
}

